import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@bb-ui/react-library/dist/components/Dialog';
import { DialogTitle } from '@bb-ui/react-library/dist/components/DialogTitle';
import { DialogContent } from '@bb-ui/react-library/dist/components/DialogContent';
import { DialogActions } from '@bb-ui/react-library/dist/components/DialogActions';
import { DefaultButton } from '@bb-ui/react-library/dist/components/Button';
import { Expand } from '@bb-ui/icons/dist/small/Expand';
import { useStyles } from '../Authentication.styles';
import { DetailPair, describeDetails } from './auditEntryText';

/**
 * The Details cell: the pairs on one line, clipped with an ellipsis. Only
 * when clipped does the text become a button that opens them in full, as the
 * Data Dictionary sample values do.
 *
 * Overflow is measured (scrollWidth > clientWidth) rather than guessed from
 * the length, so it holds across locales and fonts.
 */
export const AuditDetailsValue: React.FunctionComponent<{
    pairs: DetailPair[];
}> = ({ pairs }) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const cellRef = React.useRef<HTMLSpanElement>(null);
    const textRef = React.useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = React.useState(false);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const dialogOpenRef = React.useRef(dialogOpen);
    dialogOpenRef.current = dialogOpen;
    const details = describeDetails(pairs);

    // Measured against the whole cell, not the text's own box, which the
    // button shares with the icon. While the dialog is open the result is
    // held, so its trigger stays for focus to return to.
    const checkTruncation = React.useCallback(() => {
        const cell = cellRef.current;
        const text = textRef.current;
        if (cell && text && !dialogOpenRef.current) {
            setIsTruncated(text.scrollWidth > cell.clientWidth);
        }
    }, []);

    React.useLayoutEffect(() => {
        checkTruncation();
    }, [checkTruncation, details]);

    // The cell can change width without the window doing so, as when the
    // side navigation opens.
    React.useEffect(() => {
        const observer = new ResizeObserver(checkTruncation);
        observer.observe(cellRef.current as Element);
        return () => observer.disconnect();
    }, [checkTruncation]);

    const textElement = (
        <span
            ref={textRef}
            className={classes.detailsValue}
            data-testid="auditDetailsText"
        >
            {details}
        </span>
    );

    if (!isTruncated) {
        return (
            <span ref={cellRef} className={classes.detailsFit}>
                {textElement}
            </span>
        );
    }

    const handleClose = () => setDialogOpen(false);

    // Named by its own text rather than an aria-label, so the spoken name
    // matches what is on screen (SC 2.5.3) and differs per row.
    return (
        <span ref={cellRef} className={classes.detailsFit}>
            <button
                type="button"
                className={classes.detailsButton}
                onClick={() => setDialogOpen(true)}
                aria-haspopup="dialog"
                data-testid="auditDetailsTrigger"
            >
                {textElement}
                <Expand
                    role="presentation"
                    className={classes.detailsExpandIcon}
                />
            </button>
            <Dialog
                maxWidth="md"
                fullWidth
                open={dialogOpen}
                onClose={handleClose}
                aria-labelledby="authentication-audit-details-dialog-title"
            >
                <DialogTitle
                    id="authentication-audit-details-dialog-title"
                    hideCloseButton
                >
                    {t('settings.authentication.audit.colDetails')}
                </DialogTitle>
                <DialogContent>
                    {/* tabIndex + role make the box keyboard-scrollable. */}
                    <div
                        className={classes.detailsDialogBox}
                        role="region"
                        aria-labelledby="authentication-audit-details-dialog-title"
                        tabIndex={0}
                    >
                        <span
                            className={classes.detailsLines}
                            data-testid="auditDetails"
                        >
                            {pairs.map(({ key, label, value }) => (
                                <span key={key} className={classes.detailsLine}>
                                    <strong>{label}:</strong> {value}
                                </span>
                            ))}
                        </span>
                    </div>
                </DialogContent>
                <DialogActions>
                    <DefaultButton onClick={handleClose}>
                        {t('general.close')}
                    </DefaultButton>
                </DialogActions>
            </Dialog>
        </span>
    );
};
